import { useEffect, useMemo } from "react";
import { onAction } from "mobx-state-tree";
import { BreakdownTableDepths } from "@repo/utils";
import { DEFAULT_DESCRIPTION_COLUMN_IDENTIFIER } from "@repo/structure";
import { useStores } from "@hooks";
import { useLibraryStore } from "../providers";
import {
  addLibraryElementQuery,
  changeLibraryWorkTypeQuery,
  removeLibraryElementQuery,
  setCompanySettingsQuery,
  updateLibraryElementNameQuery,
  updateLibraryElementParentQuery,
  updateLibraryElementHoursQuery,
} from "@query";
import { COMPANY_SETTINGS } from "@client";

export const useLibraryStructureListener = ( structure ) => {

  const { userStore } = useStores();
  const libraryStore = useLibraryStore();
  
  const updateLibraryWorkTypes = () => {
    libraryStore.setLibraryWorkTypes(
      structure.sections[0].workTypes.filter((wT) => !wT.invisible).map(wT => wT.id)
    );
  }
  
  const libraryPostActionListenerCb = ({ name, path, args }) => {
    // if (name === "removeWorkType") {
    //   // blockValueUpdates = true;
    //   // libraryStore.setLibraryWorkTypes(
    //   //   structure.usedWorkTypes.filter((id) => id !== args[0])
    //   // );
    //   updateLibraryWorkTypes();
    //   return;
    // }
    
    if(name === "setWorkTypeVisibility") {
      updateLibraryWorkTypes();
      return;
    }
    
    const { useMinMax, timeModifier } = structure.settings;
    const { usedBreakdownColumns } = structure;
    
    switch(name) {
      case "setModifier":
      case "setVisibility":
      case "setMinMax":
      case "setBreakdownColumnVisibility":
        setCompanySettingsQuery(COMPANY_SETTINGS.LIBRARY_SETTINGS, {
          useMinMax,
          timeModifier,
          columns: usedBreakdownColumns.filter(c => c.visible).map(c => c.name),
        });
        break;
      case "changeWorkType":
        // blockValueUpdates = true;
        changeLibraryWorkTypeQuery(args[0], args[1]);
        updateLibraryWorkTypes();
        // libraryStore.setLibraryWorkTypes([
        //   ...structure.usedWorkTypes.filter((id) => id !== args[0]),
        //   args[1],
        // ]);
        break;
      case "reorderWorkTypes":
        libraryStore.setLibraryWorkTypes(args[0].map((arg) => arg.id));
        break;
      // case "addWorkType":
      case "addWorkTypeToSections":
        // libraryStore.setLibraryWorkTypes(structure.usedBreakdownWorkTypes);
        // libraryStore.setLibraryWorkTypes(structure.sections[0].workTypes.filter((wT) => !wT.invisible).map(wT => wT.id));
        updateLibraryWorkTypes();
        break;
    }
    
    if (!path.length || name !== "setWorkloadValue")
      return;
    
    path = path.slice(1).split("/");
    const child = path.reduce((node, key) => node?.[key], structure);
    
    if(!child)
      return;
    
    const updateChildrenHours = (p) => {
      const values = p.values?.get(args[0].toString());
      updateLibraryElementHoursQuery(
        userStore.data.id,
        p.id,
        args[0],
        values?.min || 0,
        values?.avg || 0,
        values?.max || 0
      );
      p.children?.forEach((c) => updateChildrenHours(c))
    }
    
    path.reduce((node, key) => {
      const el = node?.[key];
      if(typeof el?.id === "number" && el.id !== child.id && typeof el?.name === "string") {
        const values = el.values?.get(args[0].toString());
        updateLibraryElementHoursQuery(
          userStore.data.id,
          el.id,
          args[0],
          values?.min || 0,
          values?.avg || 0,
          values?.max || 0
        );
      }
      return el;
    }, structure);
    updateChildrenHours(child);
  }

  const libraryPreActionListenerCb = ({ name, path, args }) => {
    
    if (!path.length)
      return;
    
    path = path.slice(1).split("/");
    const child = path.reduce((node, key) => node[key], structure);

    if(!child)
      return;
    
    const hasId = child.id && typeof child.id === "number";
    
    switch (name) {
      case "reorderChildren":
        // Send only id of reordered element
        if (args[0]?.length) {
          const movedElementIndex = args[0].findIndex(
            (arg) => arg.id === args[2]
          );
          if (movedElementIndex >= 0)
            updateLibraryElementParentQuery(
              args[2],
              typeof child.id === "string" ? 0 : child.id,
              movedElementIndex
            );
        }
        break;
      case "pushChild":
        updateLibraryElementParentQuery(
          args[0].id,
          typeof child.id === "string" ? 0 : child.id,
          args[1] || 0
        ); // @note: there should be no problems, as all library elements are unique (ids, names)
        break;
      case "setContent":
        if (hasId) updateLibraryElementNameQuery(child.id, child.name, args[0]);
        break;
      case "setDescription":
        if (hasId) updateLibraryElementNameQuery(child.id, child.name, args[1]);
        break;
      case "setName":
        if (!hasId) {
          (async () => {
            const level =
              path.slice(4).length / 2 +
              structure.sections[path[1]].startingLevel;
            const id = await addLibraryElementQuery(
              level,
              args[0],
              child.descriptions?.get(DEFAULT_DESCRIPTION_COLUMN_IDENTIFIER)?.content || "",
              child.parentOrder,
              level > BreakdownTableDepths.MODULE ? child.parentId : undefined
            );
            if (id) {
              child.setId(id);
              if (child.hasValues) {
                const iterator = child.taskValues[Symbol.iterator]();
                const wTUpdates = [];
                for (const item of iterator) {
                  if (
                    item[1]?.min > 0 ||
                    item[1]?.avg > 0 ||
                    item[1]?.max ||
                    0 > 0
                  )
                    wTUpdates.push(
                      updateLibraryElementHoursQuery(
                        userStore.data.id,
                        id,
                        item[0],
                        item[1]?.min || 0,
                        item[1]?.avg || 0,
                        item[1]?.max || 0
                      )
                    );
                }
                Promise.all(wTUpdates);
              }
            }
          })();
        } else updateLibraryElementNameQuery(child.id, args[0], child.descriptions?.get(DEFAULT_DESCRIPTION_COLUMN_IDENTIFIER)?.content || "");
        break;
      case "removeSelf":
        if (hasId) removeLibraryElementQuery(child.id);
        break;
      // case "setBreakdownVisibility":
      //   libraryStore.setLibraryWorkTypes(
      //     child.inBreakdown
      //       ? structure.usedBreakdownWorkTypes.filter((wT) => wT !== child.id)
      //       : [...structure.usedBreakdownWorkTypes, child.id]
      //   );
      //   // work type is added to structure first; this is a listener setup on section
      //   break;
    }
  };

  const preActionListener = useMemo(() => (
    structure &&
    libraryStore &&
    onAction(
      structure,
      libraryPreActionListenerCb
    )
  ), [structure]);
  
  const postActionListener = useMemo(() => (
    structure &&
    libraryStore &&
    onAction(
      structure,
      libraryPostActionListenerCb,
      true
    )
  ), [structure]);

  useEffect(() => {
    return () => {
      preActionListener && preActionListener();
      postActionListener && postActionListener();
    };
  }, [structure]);
};